import { useEffect } from 'react';

/**
 * Sets <title>, meta description, canonical URL, and Open Graph tags per page.
 * Cleans up on unmount so pages don't leak into each other.
 */
const setMeta = (name, content, useProperty = false) => {
    if (!content) return null;
    const attr = useProperty ? 'property' : 'name';
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    const created = !el;
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    const prev = el.getAttribute('content');
    el.setAttribute('content', content);
    return { el, prev, created };
};

const setCanonical = (href) => {
    if (!href) return null;
    let el = document.head.querySelector('link[rel="canonical"]');
    const created = !el;
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
    }
    const prev = el.getAttribute('href');
    el.setAttribute('href', href);
    return { el, prev, created };
};

/**
 * useSEO({ title, description, image, url, type })
 * - title: full page title (site suffix added automatically if not present)
 * - description: 150-160 chars ideal
 * - image: absolute or path — will be used for og:image
 * - url: canonical URL, defaults to window.location.href
 * - type: og:type (default "website")
 */
export default function useSEO({ title, description, image, url, type = 'website' }) {
    useEffect(() => {
        const prevTitle = document.title;
        const siteName = 'Dry Ice Bulls';
        const finalTitle = title
            ? (title.includes(siteName) ? title : `${title} | ${siteName}`)
            : prevTitle;
        const canonicalUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
        // Ensure image is absolute
        let imgUrl = image;
        if (imgUrl && !/^https?:\/\//i.test(imgUrl) && typeof window !== 'undefined') {
            imgUrl = window.location.origin + (imgUrl.startsWith('/') ? '' : '/') + imgUrl;
        }

        if (title) document.title = finalTitle;

        const created = [];
        const changed = [];

        const track = (result) => {
            if (!result) return;
            if (result.created) created.push(result.el);
            else changed.push({ el: result.el, prev: result.prev });
        };

        track(setMeta('description', description));
        track(setMeta('og:title', finalTitle, true));
        track(setMeta('og:description', description, true));
        track(setMeta('og:image', imgUrl, true));
        track(setMeta('og:url', canonicalUrl, true));
        track(setMeta('og:type', type, true));
        track(setMeta('og:site_name', siteName, true));
        track(setMeta('twitter:card', 'summary_large_image'));
        track(setMeta('twitter:title', finalTitle));
        track(setMeta('twitter:description', description));
        track(setMeta('twitter:image', imgUrl));
        track(setCanonical(canonicalUrl));

        return () => {
            if (title) document.title = prevTitle;
            // Remove any meta tags we created; restore ones we changed
            created.forEach((el) => el.remove());
            changed.forEach(({ el, prev }) => {
                if (prev == null) el.remove();
                else if (el.tagName === 'META') el.setAttribute('content', prev);
                else if (el.tagName === 'LINK') el.setAttribute('href', prev);
            });
        };
    }, [title, description, image, url, type]);
}
